import fetchData from '../../fetch';

const DEFAULT_CHUNK_SIZE = 2 * 1024 * 1024; // 2MB
const MAX_CHUNK_SIZE = 8 * 1024 * 1024; // 8MB
const MIN_CHUNK_SIZE = 1 * 1024 * 1024; // 1MB

export class Uploader {
    constructor(options) {
        this.useTransferAcceleration = options.useTransferAcceleration;
        options.threadsQuantity = options.threadsQuantity || 0;
        this.threadsQuantity = Math.min(options.threadsQuantity || 5, 15);
        this.timeout = 0;
        this.file = options.file;
        this.chunkSize = Math.max(
            options.chunkSize || this.calculateChunkSize(this.file.size),
            MIN_CHUNK_SIZE
        );
        this.fileName = options.fileName;
        this.aborted = false;
        this.paused = false;
        this.uploadedSize = 0;
        this.progressCache = {};
        this.activeConnections = {};
        this.parts = [];
        this.partsCount = 0;
        this.uploadedParts = options.uploadedParts || []; // Accept previously uploaded parts
        this.fileId = options.fileId || null; // Accept fileId if provided
        this.fileKey = options.fileKey || null; // Accept fileKey if provided
        this.retryCount = 0;
        this.maxRetryBeforePause = 10;
        this.consecutiveErrors = 0;
        this.maxConsecutiveErrors = 15;
        this.onProgressFn = () => {};
        this.onErrorFn = () => {};
        this.onPauseFn = () => {};
        this.onResumeFn = () => {};
    }

    calculateChunkSize(fileSize) {
        return fileSize <= 50 * 1024 * 1024
            ? DEFAULT_CHUNK_SIZE
            : Math.min(MAX_CHUNK_SIZE, fileSize / 10);
    }

    async start() {
        if (this.paused) {
            console.warn('Cannot start while paused. Resume instead.');
            return;
        }
        await this.initialize();
    }

    async initialize() {
        try {
            let fileName = this.file.name;

            const videoInitializationUploadInput = { name: fileName };

            const initializeResponse = await fetchData.Event.UploadVideoMpInit({
                method: 'POST',
                body: JSON.stringify(videoInitializationUploadInput)
            });

            const AWSFileDataOutput = initializeResponse;
            this.fileId = AWSFileDataOutput.fileId;
            this.fileKey = AWSFileDataOutput.fileKey;

            const numberOfParts = Math.ceil(this.file.size / this.chunkSize);
            this.partsCount = numberOfParts;
            const AWSMultipartFileDataInput = {
                fileId: this.fileId,
                fileKey: this.fileKey,
                parts: numberOfParts
            };
            const urlsResponse = await fetchData.Event.UploadVideoMpPresignedUrl({
                method: 'POST',
                body: JSON.stringify(AWSMultipartFileDataInput)
            });

            this.parts.push(...urlsResponse.parts);

            // Remove already uploaded parts
            this.parts = this.parts.filter(
                (part) =>
                    !this.uploadedParts.some(
                        (uploadedPart) => uploadedPart.PartNumber === part.PartNumber
                    )
            );

            this.sendNext();
        } catch (error) {
            await this.complete(error);
        }
    }

    sendNext(retry = 0) {
        if (this.consecutiveErrors >= this.maxConsecutiveErrors) {
            this.pause(new Error('Too many consecutive errors. Upload paused.'));
            return;
        }

        const activeConnections = Object.keys(this.activeConnections).length;
        if (activeConnections >= this.threadsQuantity || this.paused || this.aborted) {
            return;
        }

        if (!this.parts.length) {
            if (!activeConnections) {
                this.complete();
            }
            return;
        }

        const part = this.parts.pop();
        if (this.file && part) {
            const sentSize = (part.PartNumber - 1) * this.chunkSize;
            const chunk = this.file.slice(sentSize, sentSize + this.chunkSize);

            const sendChunkStarted = () => this.sendNext();

            this.sendChunk(chunk, part, sendChunkStarted)
                .then(() => {
                    this.retryCount = 0;
                    this.consecutiveErrors = 0;
                    this.sendNext();
                })
                .catch((error) => {
                    this.consecutiveErrors++;
                    if (retry < this.maxRetryBeforePause) {
                        this.retryCount++;
                        const waitTime = 2 ** this.retryCount * 100;
                        setTimeout(() => {
                            this.parts.push(part);
                            this.sendNext(retry + 1);
                        }, waitTime);
                    } else {
                        this.pause(error);
                    }
                });
        }
    }

    pause(error = null) {
        if (this.paused) return;

        this.paused = true;
        Object.keys(this.activeConnections).forEach((id) => {
            this.activeConnections[id].abort();
        });

        //console.info("UPLOAD PAUSED. Error:", error, "Remaining parts:", this.parts.length);
        //console.info("SUCCESSFUL UPLOADS SO FAR:", this.uploadedParts.length);

        this.onPauseFn({
            error,
            uploadedSize: this.uploadedSize,
            partsRemaining: [...this.parts],
            partsUploaded: [...this.uploadedParts],
            fileId: this.fileId,
            fileKey: this.fileKey,
            partsCount: this.partsCount
        });
    }

    resume(uploadedParts = this.uploadedParts) {
        if (!this.paused) {
            console.warn('Uploader is not paused.');
            return;
        }

        this.paused = false;
        this.consecutiveErrors = 0;
        this.retryCount = 0;

        // Update uploaded parts and remove them from the remaining parts
        this.uploadedParts = uploadedParts;
        this.parts = this.parts.filter(
            (part) =>
                !this.uploadedParts.some(
                    (uploadedPart) => uploadedPart.PartNumber === part.PartNumber
                )
        );

        this.onResumeFn();
        this.sendNext();
    }

    handleProgress(part, event) {
        if (this.file) {
            if (event.type === 'progress' || event.type === 'error' || event.type === 'abort') {
                this.progressCache[part] = event.loaded;
            }

            if (event.type === 'uploaded') {
                this.uploadedSize += this.progressCache[part] || 0;
                delete this.progressCache[part];
            }

            const inProgress = Object.keys(this.progressCache)
                .map(Number)
                .reduce((memo, id) => (memo += this.progressCache[id]), 0);

            const sent = Math.min(this.uploadedSize + inProgress, this.file.size);
            const total = this.file.size;
            const percentage = Math.round((sent / total) * 100);

            this.onProgressFn({
                sent: sent,
                total: total,
                percentage: percentage
            });
        }
    }

    async complete(error) {
        if (error && !this.aborted) {
            this.onErrorFn(error);
            return;
        }

        try {
            await this.sendCompleteRequest();
        } catch (error) {
            this.onErrorFn(error);
        }
    }

    async sendCompleteRequest() {
        if (this.fileId && this.fileKey) {
            const videoFinalizationMultiPartInput = {
                fileId: this.fileId,
                fileKey: this.fileKey,
                parts: this.uploadedParts
            };

            await fetchData.Event.UploadVideoMpFins({
                method: 'POST',
                body: JSON.stringify(videoFinalizationMultiPartInput)
            });
        }
    }

    sendChunk(chunk, part, sendChunkStarted) {
        return new Promise((resolve, reject) => {
            this.upload(chunk, part, sendChunkStarted)
                .then((status) => {
                    if (status !== 200) {
                        reject(new Error('Failed chunk upload'));
                        return;
                    }
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    upload(file, part, sendChunkStarted) {
        return new Promise((resolve, reject) => {
            const throwXHRError = (error, part, abortFx) => {
                delete this.activeConnections[part.PartNumber - 1];
                reject(error);
                window.removeEventListener('offline', abortFx);
            };
            if (this.fileId && this.fileKey) {
                if (!window.navigator.onLine) reject(new Error('System is offline'));

                const xhr = (this.activeConnections[part.PartNumber - 1] = new XMLHttpRequest());
                xhr.timeout = this.timeout;
                sendChunkStarted();

                const progressListener = this.handleProgress.bind(this, part.PartNumber - 1);

                xhr.upload.addEventListener('progress', progressListener);

                xhr.addEventListener('error', progressListener);
                xhr.addEventListener('abort', progressListener);
                xhr.addEventListener('loadend', progressListener);

                xhr.open('PUT', part.signedUrl);
                const abortXHR = () => xhr.abort();
                xhr.onreadystatechange = () => {
                    if (xhr.readyState === 4 && xhr.status === 200) {
                        const ETag = xhr.getResponseHeader('ETag');
                        if (ETag) {
                            const uploadedPart = {
                                PartNumber: part.PartNumber,
                                ETag: ETag.replaceAll('"', '')
                            };

                            this.uploadedParts.push(uploadedPart);

                            resolve(xhr.status);
                            delete this.activeConnections[part.PartNumber - 1];
                            window.removeEventListener('offline', abortXHR);
                        }
                    }
                };

                xhr.onerror = (error) => {
                    throwXHRError(error, part, abortXHR);
                };
                xhr.ontimeout = (error) => {
                    throwXHRError(error, part, abortXHR);
                };
                xhr.onabort = () => {
                    throwXHRError(new Error('Upload canceled by user or system'), part);
                };
                window.addEventListener('offline', abortXHR);
                xhr.send(file);
            }
        });
    }

    onProgress(onProgress) {
        this.onProgressFn = onProgress;
        return this;
    }

    onError(onError) {
        this.onErrorFn = onError;
        return this;
    }

    onPause(onPause) {
        this.onPauseFn = onPause;
        return this;
    }

    onResume(onResume) {
        this.onResume = onResume;
        return this;
    }

    abort() {
        Object.keys(this.activeConnections)
            .map(Number)
            .forEach((id) => {
                this.activeConnections[id].abort();
            });

        this.aborted = true;
    }
}
